// RoleFormModal — Create / Edit modal for Role.
//
// CHG-1663 / ITER-3772 (Wave 1 / Tier 1 — Roles port).
//
// Hand-rolled exception to the generator-first doctrine. The v2 page
// generator emits ListPage + DetailPage scaffolds from
// role.entity-hint.yaml but does not emit forms (forms ride a separate
// generator-emission path through specs/seeds/dashboard-v2/wizards/*
// via v2genwiz.py; create/edit-by-PATCH single-screen forms have no
// generator at all yet). v2's composition FormDialog ALSO can't render
// text/textarea/number — its field-primitive registry covers only
// domain primitives (email/phone/date/amount/...). Until either the
// generator gains "simple-form" emission or FormDialog gains
// text/textarea/number primitives, the Role form is a small inline
// component with native inputs.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  rolesApi,
  type Role,
  type RoleCreatePayload,
  type RoleUpdatePayload,
} from "../../api/roles";
import { ModalShell } from "./ModalShell";

const SLUG_RE = /^[a-z][a-z0-9-]{0,49}$/;

interface RoleFormState {
  slug: string;
  display_name: string;
  description: string;
  priority: number;
}

export function RoleFormModal({
  mode,
  initial,
  onClose,
}: {
  mode: "create" | "edit";
  initial?: Role;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<RoleFormState>({
    slug: initial?.slug ?? "",
    display_name: initial?.display_name ?? "",
    description: initial?.description ?? "",
    priority: initial?.priority ?? 100,
  });
  const [slugError, setSlugError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (payload: RoleCreatePayload) => rolesApi.createRole(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roles", "list"] });
      onClose();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail;
      setServerError(detail ?? "Could not create role.");
    },
  });

  const update = useMutation({
    mutationFn: (payload: RoleUpdatePayload) =>
      rolesApi.updateRole(initial!.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roles", "list"] });
      qc.invalidateQueries({ queryKey: ["roles", "detail"] });
      onClose();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail;
      setServerError(detail ?? "Could not update role.");
    },
  });

  const isPending = create.isPending || update.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setServerError(null);
    if (mode === "create") {
      if (!SLUG_RE.test(form.slug)) {
        setSlugError(
          "Slug must start with a lowercase letter and contain only lowercase letters, numbers, or hyphens (max 50 chars).",
        );
        return;
      }
      setSlugError(null);
      const payload: RoleCreatePayload = {
        slug: form.slug,
        display_name: form.display_name,
        priority: form.priority,
      };
      if (form.description.trim()) payload.description = form.description;
      create.mutate(payload);
    } else {
      const payload: RoleUpdatePayload = {
        display_name: form.display_name,
        description: form.description || null,
        priority: form.priority,
      };
      update.mutate(payload);
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: "6px 10px",
    fontSize: 13,
    borderRadius: "var(--radius-sm, 6px)",
    border: "1px solid var(--color-border)",
    background: "var(--color-surface)",
    color: "var(--color-text)",
    fontFamily: "inherit",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-muted)",
    marginBottom: 4,
    display: "block",
  };

  return (
    <ModalShell
      title={mode === "create" ? "Create role" : `Edit role — ${initial?.display_name ?? ""}`}
      onClose={onClose}
    >
      <form
        onSubmit={onSubmit}
        data-form="role-form"
        style={{
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          flex: 1,
          overflowY: "auto",
        }}
      >
        <label>
          <span style={labelStyle}>
            Slug<span style={{ color: "var(--color-danger, #b91c1c)" }}> *</span>
          </span>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => {
              setForm((f) => ({ ...f, slug: e.target.value }));
              if (slugError) setSlugError(null);
            }}
            disabled={mode === "edit"}
            required
            placeholder="finance-reviewer"
            style={{
              ...inputStyle,
              borderColor: slugError ? "var(--color-danger, #b91c1c)" : undefined,
              opacity: mode === "edit" ? 0.6 : 1,
              cursor: mode === "edit" ? "not-allowed" : "text",
              width: "100%",
            }}
          />
          {slugError ? (
            <span
              style={{
                display: "block",
                marginTop: 4,
                fontSize: 12,
                color: "var(--color-danger, #b91c1c)",
              }}
            >
              {slugError}
            </span>
          ) : (
            <span
              style={{
                display: "block",
                marginTop: 4,
                fontSize: 11,
                color: "var(--color-text-muted)",
              }}
            >
              Lowercase, hyphenated. Immutable after creation.
            </span>
          )}
        </label>

        <label>
          <span style={labelStyle}>
            Display name<span style={{ color: "var(--color-danger, #b91c1c)" }}> *</span>
          </span>
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            required
            style={{ ...inputStyle, width: "100%" }}
          />
        </label>

        <label>
          <span style={labelStyle}>Description</span>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={3}
            style={{
              ...inputStyle,
              width: "100%",
              padding: 8,
              resize: "vertical",
            }}
          />
        </label>

        <label>
          <span style={labelStyle}>Priority</span>
          <input
            type="number"
            value={form.priority}
            onChange={(e) =>
              setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))
            }
            style={{ ...inputStyle, width: 120 }}
          />
          <span
            style={{
              display: "block",
              marginTop: 4,
              fontSize: 11,
              color: "var(--color-text-muted)",
            }}
          >
            Higher numbers outrank lower ones for permission-resolution
            tie-breaks. Per BUG-1011 doctrine, system roles ship at
            5000/3000/1000; keep custom roles below 5000 to avoid
            shadowing platform-admin.
          </span>
        </label>

        {serverError ? (
          <div
            role="alert"
            data-role="server-error"
            style={{
              padding: "8px 10px",
              fontSize: 13,
              color: "var(--color-danger, #b91c1c)",
              background: "var(--color-danger-bg, #fef2f2)",
              borderRadius: "var(--radius-sm, 6px)",
            }}
          >
            {serverError}
          </div>
        ) : null}
      </form>

      <footer
        style={{
          display: "flex",
          gap: 8,
          padding: "12px 20px",
          borderTop: "1px solid var(--color-border-subtle)",
          justifyContent: "flex-end",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="role-form"
          onClick={onSubmit}
          disabled={isPending}
          data-action={mode === "create" ? "create-role" : "save-role"}
          className="btn btn-primary"
        >
          {isPending ? "Saving…" : mode === "create" ? "Create role" : "Save changes"}
        </button>
      </footer>
    </ModalShell>
  );
}
